<script>
import LandLeaseDeadlineService from "@/services/LandLeaseDeadlineService";
import { EventBus } from "@/libraries/EventBus";

export default {
    props: {
        landLease: { type: Object, required: true },
        initAddForm: { type: Boolean, default: false }
    },
    data() {
        return {
            showAddForm: this.initAddForm,
            editing: false,
            saving: false,
            reloading: false,
            newDeadline: {
                id: null,
                land_lease_id: this.landLease.id,
                deadline_name: null,
                deadline_date: null,
                remind_before_days: 45
            }
        };
    },
    computed: {
        deadlines() {
            return this.landLease.land_lease_deadlines || [];
        }
    },
    methods: {
        statusBadgeType(dl) {
            if (dl.completed) return "success";
            if (dl.deadline_status === "passed") return "danger";
            if (dl.deadline_status === "warning") return "warning";
            return "info";
        },
        statusLabel(dl) {
            if (dl.completed) return this.$t("land_leases.deadline_completed");
            if (dl.deadline_status === "passed")
                return this.$t("land_leases.deadline_passed");
            if (dl.deadline_status === "warning")
                return this.$t("land_leases.deadline_warning");
            return this.$t("land_leases.deadline_normal");
        },
        timeToDeadlineText(dl) {
            const v = dl.time_to_deadline;
            if (!v) return null;
            const neg =
                (v.years || 0) < 0 || (v.months || 0) < 0 || (v.days || 0) < 0;
            const p = [];
            const y = Math.abs(v.years || 0);
            const m = Math.abs(v.months || 0);
            const d = Math.abs(v.days || 0);
            if (y) p.push(y + " " + this.$tc("exp_module.years", y));
            if (m) p.push(m + " " + this.$tc("exp_module.months", m));
            if (d || !p.length)
                p.push(d + " " + this.$tc("exp_module.days", d));
            const text = p.join(", ");
            return neg
                ? this.$t("land_leases.deadline_overdue_by", { time: text })
                : text;
        },
        dotColor(dl) {
            if (dl.completed) return "text-success";
            if (dl.deadline_status === "passed") return "text-danger";
            if (dl.deadline_status === "warning") return "text-warning";
            return "text-primary";
        },
        resetForm() {
            this.editing = false;
            this.newDeadline = {
                id: null,
                land_lease_id: this.landLease.id,
                deadline_name: null,
                deadline_date: null,
                remind_before_days: 45
            };
        },
        editDeadline(dl) {
            this.editing = true;
            this.newDeadline = {
                id: dl.id,
                land_lease_id: dl.land_lease_id,
                deadline_name: dl.deadline_name,
                deadline_date: dl.deadline_date.substring(0, 10),
                remind_before_days: dl.remind_before_days
            };
            this.showAddForm = true;
        },
        async saveDeadline() {
            this.saving = true;
            try {
                if (this.editing) {
                    await LandLeaseDeadlineService.PUT(this.newDeadline);
                    this.$notify({
                        message: this.$t("land_leases.notify_deadline_updated"),
                        timeout: 5000,
                        icon: "far fa-check",
                        type: "success"
                    });
                } else {
                    await LandLeaseDeadlineService.POST(this.newDeadline);
                    this.$notify({
                        message: this.$t("land_leases.notify_deadline_created"),
                        timeout: 5000,
                        icon: "far fa-check",
                        type: "success"
                    });
                }
                this.showAddForm = false;
                this.resetForm();
                this.reloading = true;
                EventBus.$emit("land-lease-deadlines-changed");
            } catch (e) {
                console.log("saveDeadline e", e);
            }
            this.saving = false;
        },
        toggleCompleted(dl) {
            const markComplete = !dl.completed;
            const text = markComplete
                ? this.$t("land_leases.confirm_complete_deadline")
                : this.$t("land_leases.confirm_uncomplete_deadline");

            this.$swal({
                icon: "question",
                text: text,
                showConfirmButton: true,
                showCancelButton: true
            }).then(async confirmed => {
                if (confirmed.isConfirmed) {
                    try {
                        await LandLeaseDeadlineService.PUT({
                            id: dl.id,
                            completed: markComplete ? 1 : 0
                        });
                        this.$notify({
                            message: markComplete
                                ? this.$t(
                                      "land_leases.notify_deadline_completed"
                                  )
                                : this.$t(
                                      "land_leases.notify_deadline_uncompleted"
                                  ),
                            timeout: 5000,
                            icon: "far fa-check",
                            type: "success"
                        });
                        this.reloading = true;
                        EventBus.$emit("land-lease-deadlines-changed");
                    } catch (e) {
                        console.log("toggleCompleted e", e);
                    }
                }
            });
        },
        deleteDeadline(dl) {
            this.$swal({
                icon: "warning",
                text: this.$t("land_leases.confirm_delete_deadline"),
                showConfirmButton: true,
                showCancelButton: true
            }).then(async confirmed => {
                if (confirmed.isConfirmed) {
                    try {
                        await LandLeaseDeadlineService.DELETE(dl.id);
                        this.$notify({
                            message: this.$t(
                                "land_leases.notify_deadline_deleted"
                            ),
                            timeout: 5000,
                            icon: "far fa-check",
                            type: "success"
                        });
                        this.reloading = true;
                        EventBus.$emit("land-lease-deadlines-changed");
                    } catch (e) {
                        console.log("deleteDeadline e", e);
                    }
                }
            });
        }
    },
    watch: {
        "landLease.land_lease_deadlines"() {
            this.reloading = false;
        }
    }
};
</script>

<template>
    <modal
        size="full"
        :show="true"
        @close="$emit('close')"
        class="budget-modal"
    >
        <template slot="header">
            {{ $t("land_leases.deadlines") }}
        </template>

        <template slot="default">
            <!-- Header with add button -->
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 class="mb-0">
                    {{ $t("land_leases.deadlines_overview") }}
                </h4>
                <base-button
                    size="sm"
                    type="primary"
                    outline
                    @click="
                        resetForm();
                        showAddForm = !showAddForm;
                    "
                >
                    <i class="far fa-plus"></i>
                    {{ $t("land_leases.add_deadline") }}
                </base-button>
            </div>

            <!-- Add form -->
            <div v-if="showAddForm" class="border rounded p-3 mb-3 bg-lighter">
                <FormulateForm @submit="saveDeadline" v-model="newDeadline">
                    <div class="row">
                        <div class="col-12 col-md-4">
                            <FormulateInput
                                name="deadline_name"
                                type="text"
                                :label="$t('land_leases.deadline_name')"
                                :placeholder="
                                    $t('land_leases.placeholder_deadline_name')
                                "
                                validation="required"
                                :validation-name="
                                    $t('land_leases.deadline_name')
                                "
                            />
                        </div>
                        <div class="col-12 col-md-4">
                            <FormulateInput
                                name="deadline_date"
                                type="date"
                                :label="$t('land_leases.deadline_date')"
                                validation="required"
                                :validation-name="
                                    $t('land_leases.deadline_date')
                                "
                            />
                        </div>
                        <div class="col-12 col-md-4">
                            <FormulateInput
                                name="remind_before_days"
                                type="number"
                                :label="$t('land_leases.remind_before_days')"
                                :placeholder="
                                    $t(
                                        'land_leases.placeholder_remind_before_days'
                                    )
                                "
                                validation="required"
                                :validation-name="
                                    $t('land_leases.remind_before_days')
                                "
                            />
                        </div>
                    </div>
                    <div class="text-right">
                        <base-button
                            size="sm"
                            type="secondary"
                            outline
                            @click="
                                showAddForm = false;
                                resetForm();
                            "
                        >
                            {{ $t("cancel") }}
                        </base-button>
                        <base-button
                            size="sm"
                            type="primary"
                            :loading="saving"
                            native-type="submit"
                        >
                            {{ $t("save") }}
                        </base-button>
                    </div>
                </FormulateForm>
            </div>

            <!-- Deadlines table -->
            <v-loader :loading="reloading" v-if="reloading" />
            <template v-else>
                <div v-if="deadlines.length" class="table-responsive">
                    <table class="table align-items-center">
                        <thead class="thead-light">
                            <tr>
                                <th></th>
                                <th>{{ $t("land_leases.deadline_name") }}</th>
                                <th>{{ $t("land_leases.deadline_date") }}</th>
                                <th>
                                    {{ $t("land_leases.time_to_deadline") }}
                                </th>
                                <th>
                                    {{ $t("land_leases.remind_before_days") }}
                                </th>
                                <th>{{ $t("land_leases.hdr_status") }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="dl in deadlines"
                                :key="dl.id"
                                :class="{
                                    'bg-lighter text-muted': dl.completed
                                }"
                            >
                                <td>
                                    <i
                                        class="fas fa-circle fa-xs"
                                        :class="dotColor(dl)"
                                    ></i>
                                </td>
                                <td>
                                    {{ dl.deadline_name }}
                                </td>
                                <td>
                                    {{
                                        $d(new Date(dl.deadline_date), "short")
                                    }}
                                </td>
                                <td class="small">
                                    <template v-if="dl.time_to_deadline">
                                        <span
                                            :class="
                                                dl.deadline_status === 'passed'
                                                    ? 'text-danger'
                                                    : ''
                                            "
                                        >
                                            {{ timeToDeadlineText(dl) }}
                                        </span>
                                    </template>
                                    <template v-else>---</template>
                                </td>
                                <td>
                                    {{
                                        $t("land_leases.remind_days_value", {
                                            days: dl.remind_before_days
                                        })
                                    }}
                                </td>
                                <td>
                                    <badge :type="statusBadgeType(dl)">
                                        {{ statusLabel(dl) }}
                                    </badge>
                                    <div
                                        v-if="dl.completed"
                                        class="small text-muted mt-1"
                                    >
                                        <span v-if="dl.completed_datetime">{{
                                            $d(
                                                new Date(dl.completed_datetime),
                                                "short"
                                            )
                                        }}</span>
                                        <span v-if="dl.completed_user">
                                            - {{ dl.completed_user.name }}</span
                                        >
                                    </div>
                                </td>
                                <td class="text-right text-nowrap">
                                    <base-button
                                        size="sm"
                                        type="primary"
                                        outline
                                        @click="editDeadline(dl)"
                                    >
                                        <i class="far fa-pencil"></i>
                                    </base-button>
                                    <base-button
                                        size="sm"
                                        :type="
                                            dl.completed ? 'warning' : 'success'
                                        "
                                        outline
                                        @click="toggleCompleted(dl)"
                                    >
                                        <i
                                            :class="
                                                dl.completed
                                                    ? 'far fa-undo'
                                                    : 'far fa-check'
                                            "
                                        ></i>
                                    </base-button>
                                    <base-button
                                        size="sm"
                                        type="danger"
                                        outline
                                        @click="deleteDeadline(dl)"
                                    >
                                        <i class="far fa-trash"></i>
                                    </base-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="text-muted">
                    {{ $t("land_leases.no_deadlines") }}
                </div>
            </template>
        </template>
    </modal>
</template>
